import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import {
  CHUNK_RECOVERY_BOOTED_FLAG,
  CHUNK_RECOVERY_QUERY_PARAM,
} from '../consts';
import {
  clearAttemptCount,
  clearPendingOutcome,
  getCleanPathWithoutBust,
  readPendingOutcome,
  safeGetSessionStorage,
} from '../utils';

/**
 * 청크 로드 실패 자동복구의 React 측 글루 (ZEP-655, quiz-app 먼저).
 *
 * 복구 엔진은 _document 인라인 스크립트(chunkRecoveryBootstrapScript)다 — 진짜 백지인
 * 부팅 전(초기 로드/하드 네비) 청크 실패를 어떤 청크보다 먼저 잡아 cache-bust 리로드(새 버전)
 * → 실패 시 포기로 처리한다. 부팅된 앱의 청크 에러는 화면이 멀쩡해 복구 불필요하고, Sentry SDK 가
 * 평소대로 관측한다.
 *
 * 이 훅(마운트 = 하이드레이션 성공 = 부팅 완료)이 하는 일:
 * 1. 부팅 완료 플래그를 세워 인라인 스크립트가 손을 떼게 한다(멀쩡한 페이지 리로드 방지).
 * 2. 캐시버스트로 되돌아온 문서면 URL·router.query 에서 파라미터를 지운다(주소창/공유/분석/CF키 오염 방지).
 * 3. 시도 카운트를 지운다 — 복구 성공했으니 다음 독립 에피소드는 새로 cache-bust 를 시도하게.
 * 4. 인라인이 부팅 전에 남긴 pending 결과를 Sentry 로 flush 한다(리로드에 취소되지 않게 미룬 보고).
 */
export function useChunkLoadRecovery(): void {
  const router = useRouter();

  useEffect(() => {
    // 앱이 부팅(interactive)됐음을 인라인 스크립트에 알린다 — 이후 청크 실패엔 인라인이 손 뗌.
    (window as unknown as Record<string, boolean>)[CHUNK_RECOVERY_BOOTED_FLAG] =
      true;

    // 주소창/공유 URL 에서 캐시버스트 파라미터를 즉시 제거한다. history.replaceState 는 동기라
    // 정적 최적화 페이지(router.isReady=false)에서도 확실하다 — router.replace 만 쓰면 그런
    // 페이지에서 파라미터가 주소창에 그대로 남는다(dev-3 실측). router.query 동기화는 아래 별도 effect.
    const cleanPath = getCleanPathWithoutBust(
      window.location.href,
      CHUNK_RECOVERY_QUERY_PARAM,
    );
    if (cleanPath) {
      window.history.replaceState(window.history.state, '', cleanPath);
    }

    const storage = safeGetSessionStorage();
    // 복구 성공(=부팅됨)이므로 시도 이력을 비운다. 안 지우면 같은 buildId 의 별개 실패가 곧장 포기됨.
    clearAttemptCount(storage);

    // 부팅 전 인라인 스크립트가 남긴 복구 결과를 Sentry 로 flush 한다. message 가 청크 패턴이라
    // applyChunkLoadFingerprint 가 단계별(cache-bust/failed)로 그룹핑한다.
    const pending = readPendingOutcome(storage);
    if (pending) {
      clearPendingOutcome(storage);
      logger.captureError(new Error(pending.message), {
        level: pending.stage === 'failed' ? 'fatal' : 'warning',
        tags: { chunk_recovery: pending.stage },
        extra: { buildId: pending.buildId, source: 'bootstrap' },
      });
    }
  }, []);

  // router.query 동기화: history.replaceState 는 주소창만 정리하고 Next 의 router.query 에는
  // 파라미터가 남아, query 를 통째로 다음 URL 로 넘기는 흐름(로그인→회원가입 등)이 이를 재노출한다
  // (Codex P2). 정적 페이지는 첫 마운트에 isReady=false 라 그때 router.replace 는 무시되므로
  // isReady 된 뒤 router.query 를 직접 보고 제거한다.
  useEffect(() => {
    if (!router.isReady) return;
    if (router.query[CHUNK_RECOVERY_QUERY_PARAM] === undefined) return;
    const nextQuery = { ...router.query };
    delete nextQuery[CHUNK_RECOVERY_QUERY_PARAM];
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    );
  }, [router.isReady, router]);
}
